// Users CSV export — domain wrapper over the generic `useAsyncExport`
// state machine. All the flow logic (POST → poll → download, cap timer,
// terminal handling) lives in `../useAsyncExport.ts`; this file only pins
// the two endpoint paths and the user-facing error copy.
//
// Preserves the previous public shape (`useUsersExport()`, `USERS_EXPORT_*`
// re-exports) so existing call sites don't need to change.

import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import {
  useAsyncExport,
  ASYNC_EXPORT_POLL_INTERVAL_MS,
  ASYNC_EXPORT_CLIENT_CAP_MS,
} from "../useAsyncExport";
import type { AsyncExportState } from "../useAsyncExport.types";
import type { UsersExportRequest } from "../../types/users";

export const USERS_EXPORT_START_KEY = "USERS_EXPORT_START";
export const USERS_EXPORT_STATUS_KEY = "USERS_EXPORT_STATUS";
export const USERS_EXPORT_POLL_INTERVAL_MS = ASYNC_EXPORT_POLL_INTERVAL_MS;
export const USERS_EXPORT_CLIENT_CAP_MS = ASYNC_EXPORT_CLIENT_CAP_MS;

const buildStatusPath = (exportId: string): string =>
  API_ROUTES.USERS_EXPORT_STATUS.replace("{export_id}", exportId);

/**
 * Start + poll a Users CSV export. See `useAsyncExport` for the state
 * machine contract — this wrapper only wires the two paths and the
 * user-visible error copy.
 */
export function useUsersExport(): AsyncExportState<UsersExportRequest> {
  return useAsyncExport<UsersExportRequest>({
    startPath: API_ROUTES.USERS_EXPORT_START,
    statusPath: buildStatusPath,
    strings: {
      startError: AppStrings.USERS_EXPORT_START_ERROR,
      failedDefault: AppStrings.USERS_EXPORT_FAILED_DEFAULT,
      timeout: AppStrings.USERS_EXPORT_TIMEOUT,
    },
    queryKeys: {
      start: USERS_EXPORT_START_KEY,
      status: USERS_EXPORT_STATUS_KEY,
    },
  });
}

export default useUsersExport;
